<script lang="ts" setup>
import { GpAlert, GpBtn, GpTextInput } from '@gportal/uikit';
import { create } from '@bufbuild/protobuf';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import BillingProfileCard from '@panel/components/app/BillingProfileCard.vue';
import AddProjectBillingProfileModal from '@panel/components/project/create/AddProjectBillingProfileModal.vue';
import ProjectSettingsLayout from '@panel/layouts/Layout.Project.Settings.vue';
import AvatarUpload from '@panel/modules/AvatarUpload.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import {
  type BillingProfile,
  BillingProfileSchema,
} from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { ProjectSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import Section from 'gpcore-uikit/components/Component.Section.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import Size from 'gpcore-uikit/interfaces/Size';
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

useProjectViewTitle('Project settings');

const editProject = reactive<{
  id: string;
  name: string;
  billingProfile: BillingProfile;
}>({
  id: '',
  name: '',
  billingProfile: null,
});

const projectStore = useProjectStore();
const project = computed(() => projectStore.project);
const reloadForm = () => {
  editProject.id = project.value.id;
  editProject.name = project.value.name;
  editProject.billingProfile = project.value.billingProfile;
};

const { addNotification } = useNotifications();

const submitEdit = () => {
  const project = create(ProjectSchema);
  project.id = editProject.id;
  project.name = editProject.name;
  project.billingProfile = editProject.billingProfile;

  if (editProject.billingProfile.id !== '') {
    const billingProfile = create(BillingProfileSchema);
    billingProfile.id = editProject.billingProfile.id;
    project.billingProfile = billingProfile;
  }

  projectStore
    .updateProject(project)
    .then(() => {
      addNotification({
        message: 'The project settings have been saved.',
        type: 'success',
      });
    })
    .catch((error: ServiceError) => {
      addNotification({ message: error.message, type: 'danger' });
    });
};

const router = useRouter();
const deleteProject = reactive({
  name: '',
});
const defaultProjectId = computed(() => projectStore.defaultProjectId);
const submitDelete = () => {
  if (deleteProject.name === project.value.name) {
    deleteProject.name = '';
    projectStore
      .deleteProject(project.value)
      .then(() => {
        projectStore.changeProject(defaultProjectId.value);
        router.push({ name: 'Home' });
      })
      .catch((error: ServiceError) => {
        addNotification({
          message: error.message,
          type: 'danger',
        });
      });
  }
};

const userStore = useUserStore();
const showAddBillingProfileModal = ref(false);
const billingProfiles = computed((): Array<BillingProfile> => userStore.billingProfiles);
const changeBillingProfile = (profile: BillingProfile) => {
  editProject.billingProfile = profile;
};

const handleBillingProfileCreated = async (id: string) => {
  showAddBillingProfileModal.value = false;
  await userStore.reloadBillingProfiles();
  const billingProfile = userStore.billingProfiles.find((profile) => profile.id === id);
  changeBillingProfile(billingProfile);
};

const onAvatarUpdate = async () => {
  // Reload projects to update the avatar
  await projectStore.reloadProjects();
  // make sure that after reload of all projects, the current project is set again with the updated avatar
  projectStore.setProject(project.value);
};

const route = useRoute();
onMounted(async () => {
  await reloadForm();
  await userStore.reloadBillingProfiles();

  await nextTick();

  if (route.hash.includes('billing')) {
    const billingSection = document.getElementById('billing');
    if (billingSection) {
      document.documentElement.scrollTo({ top: billingSection.offsetTop - 50, behavior: 'smooth' });
    }
  }
});
</script>

<template>
  <ProjectSettingsLayout :content-size="Size.Tiny">
    <template #buttons>
      <GpBtn size="sm" @click="submitEdit()" :icon="['fas', 'fa-save']" label="Save" />
    </template>
    <template #content>
      <template v-if="project">
        <Section :size="Size.Tiny" title="Edit project">
          <GpTextInput
            class="tw-mb-4"
            label="Project ID"
            v-model="editProject.id"
            name="id"
            input-id="project-settings-id"
            readonly
            transparent
          />
          <GpTextInput
            class="tw-mb-4"
            label="Project name"
            v-model="editProject.name"
            name="name"
            input-id="project-settings-name"
            transparent
          />
        </Section>
        <Section :size="Size.Tiny" id="billing" title="Payment settings">
          <p class="tw-mb-4">
            Changing the billing profile affects the current payment cycle and all following bills.
          </p>
          <GpAlert
            v-if="!projectStore.hasBillingProfile"
            title="Warning"
            message="Creating new nodes is not possible until a billing profile is set."
            class="tw-mb-4"
            alert-type="danger"
            size="sm"
          />
          <BillingProfileCard
            v-for="listBillingProfile in billingProfiles"
            :key="listBillingProfile.id"
            class="tw-mb-4"
            :billing-profile="listBillingProfile"
            :selected="
              editProject.billingProfile != null &&
              listBillingProfile.id === editProject.billingProfile.id
            "
            @selected="changeBillingProfile(listBillingProfile)"
          />
          <GpBtn
            class="tw-mt-4 tw-w-full"
            :icon="faPlus"
            label="Add billing profile"
            outline
            @click="showAddBillingProfileModal = true"
          />
        </Section>
        <Section :size="Size.Tiny" title="Project Icon">
          <AvatarUpload
            :avatar-id="project.id"
            :avatar-url="project.avatarUrl"
            :rounded="false"
            save-avatar-label="Save Icon"
            @avatar-updated="onAvatarUpdate"
          />
        </Section>
        <Section :size="Size.Tiny" title="Delete project">
          <p class="tw-mb-4">
            Enter the project name
            <strong class="tw-whitespace-pre-wrap">({{ project.name }})</strong> to delete the
            project. Deleting a project is only possible if there are no active resources.
          </p>
          <GpTextInput
            class="tw-mb-4"
            v-model="deleteProject.name"
            input-id="project-settings-delete"
            label=""
            :placeholder="project.name"
            transparent
          />
          <GpBtn
            class="tw-mt-8 tw-w-full"
            btn-type="danger"
            :icon="['fas', 'fa-trash']"
            label="Delete project"
            :disabled="project.name !== deleteProject.name"
            @click="submitDelete()"
            v-playwright="'project-settings-delete-button'"
          />
        </Section>
        <AddProjectBillingProfileModal
          :show="showAddBillingProfileModal"
          @close="showAddBillingProfileModal = false"
          @created="handleBillingProfileCreated"
        />
      </template>
    </template>
  </ProjectSettingsLayout>
</template>
